/**
 * Validation + normalization for a single `render_graph` tool call emitted by
 * the Tables Insights chat model.
 *
 * Extracted from `components/chat/utils.tsx` (#977) so the same logic can be
 * reused by `useTableChat` to detect a bad tool call server-side-round-trip
 * BEFORE it reaches rendering, and give the model one real chance to correct
 * itself with the actual row/column vocabulary — instead of only surfacing
 * the raw `GeneratorError` text to the viewer.
 */
import type { PlatinumData } from '../../../types/carbon/models/PlatinumData'
import type { GraphConfig } from '../types'
import { generateGraphOptions } from './generateGraphOptions'
import { filterNonBaseFields } from './convertPlatinumData'

export interface GraphValidationResult {
  graphConfig: GraphConfig
  isValid: boolean
  error?: string
}

/**
 * Normalize a raw (parsed-from-JSON) graph tool call against the real
 * row/column vocabulary of the table, then validate it by attempting to
 * generate chart options.
 *
 * Mutates and returns a copy of `rawConfig` — never the original object.
 */
export function validateAndNormalizeGraphCall(
  rawConfig: GraphConfig,
  allRows: string[],
  allColumns: string[],
  allRowTypes: string[],
  allColumnTypes: string[],
  effectivePlatinumData: PlatinumData | undefined,
): GraphValidationResult {
  const graphConfig: GraphConfig = { ...rawConfig }

  // Auto-detect if AI confused rows and columns
  const columnsAreActuallyRows = graphConfig.selectedColumns?.some(col => allRows.includes(col))
  const rowsAreActuallyColumns = graphConfig.selectedRows?.some(row => allColumns.includes(row))

  if (columnsAreActuallyRows || rowsAreActuallyColumns) {
    const temp = graphConfig.selectedRows
    graphConfig.selectedRows = graphConfig.selectedColumns
    graphConfig.selectedColumns = temp
  }

  const nonBaseRows = filterNonBaseFields(allRows, allRowTypes)
  const nonBaseColumns = filterNonBaseFields(allColumns, allColumnTypes)

  // Normalize: if selectedRows or selectedColumns are empty/undefined, use all non-Base fields
  if (!graphConfig.selectedRows || graphConfig.selectedRows.length === 0) {
    graphConfig.selectedRows = nonBaseRows
  }
  if (!graphConfig.selectedColumns || graphConfig.selectedColumns.length === 0) {
    graphConfig.selectedColumns = nonBaseColumns
  }

  // Normalize selectedColumn for bar/column charts (they need singular, not plural)
  if (graphConfig.chartType === 'bar' || graphConfig.chartType === 'column') {
    if (!graphConfig.selectedColumn && graphConfig.selectedColumns && graphConfig.selectedColumns.length > 0) {
      graphConfig.selectedColumn = graphConfig.selectedColumns[0]
    }
    if (!graphConfig.selectedColumn) {
      graphConfig.selectedColumn = nonBaseColumns[0]
    }
  }

  // Helper functions to convert index-based inputs to name-based (support both formats)
  const convertToName = (value: string | number | undefined, allOptions: string[]): string | undefined => {
    if (!value) return undefined
    const str = String(value)
    if (/^\d+$/.test(str)) {
      const idx = parseInt(str, 10)
      return allOptions[idx]
    }
    return str
  }

  const convertToNames = (values: (string | number)[] | undefined, allOptions: string[]): string[] | undefined => {
    if (!values || values.length === 0) return undefined
    return values.map(v => {
      const str = String(v)
      if (/^\d+$/.test(str)) {
        const idx = parseInt(str, 10)
        return allOptions[idx]
      }
      return str
    }).filter(Boolean) as string[]
  }

  if (graphConfig.selectedColumn) {
    graphConfig.selectedColumn = convertToName(graphConfig.selectedColumn, allColumns)
  }
  if (graphConfig.selectedRows && graphConfig.selectedRows.length > 0) {
    graphConfig.selectedRows = convertToNames(graphConfig.selectedRows, allRows) || []
  }
  if (graphConfig.selectedColumns && graphConfig.selectedColumns.length > 0) {
    graphConfig.selectedColumns = convertToNames(graphConfig.selectedColumns, allColumns) || []
  }

  if (effectivePlatinumData) {
    const validationResult = generateGraphOptions(effectivePlatinumData, graphConfig)
    if (!validationResult.success) {
      return {
        graphConfig,
        isValid: false,
        error: validationResult.error,
      }
    }
  }

  return {
    graphConfig,
    isValid: true,
  }
}

/**
 * Build a human-readable summary of the real row/column vocabulary, for
 * either a graceful UI fallback message or a corrective tool response fed
 * back to the model.
 */
export function describeAvailableVocabulary(allRows: string[], allColumns: string[], limit = 40): string {
  const truncate = (labels: string[]) => {
    if (labels.length <= limit) return labels.join(', ')
    return `${labels.slice(0, limit).join(', ')}, … (${labels.length} total)`
  }
  return `Available rows: ${truncate(allRows) || '(none)'}. Available columns: ${truncate(allColumns) || '(none)'}.`
}
